/* eslint-disable i18next/no-literal-string */
import { Options } from '@contentful/rich-text-react-renderer'
import classnames from 'classnames'
import { TFunction, useTranslation } from 'next-i18next'
import React, {
  Dispatch,
  ForwardedRef,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { OptionSelectorDS } from '@src/components/blocks/option-selector-ds'
import ProductCTADS from '@src/components/blocks/product-cta-ds'
import GreyBoxesDS from '@src/components/blocks/product-cta-ds/grey-boxes-ds'
import { RenderErrorMessageDS } from '@src/components/blocks/product-cta-ds/product-message-ds'
import { ProductInfoDS } from '@src/components/blocks/product-info-ds'
import SiblingProductOptionsDS from '@src/components/blocks/sibling-product-options-ds'
import { shouldUseEnterQuantityLabel } from '@src/components/cart/b2b/utils/buildSizeBreakdown.util'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { LOCALE_STANDARD_FORMAT } from '@src/config/localisation/locale-config.constants'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { ExpressPayConfig } from '@src/modules/express-pay'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import { BLOCKS, MARKS } from '@src/quince-site-common/src/constants/contentfulRichTextTypes'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import ContentfulInternationalSizeConversionGuide from '@src/quince-site-common/src/domains/contentful/contentful-international-size-conversion-guide'
import type { default as ContentfulProductMeasuringGuide } from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import Directory from '@src/quince-site-common/src/domains/Directory'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import PriceDetailsForSSR from '@src/quince-site-common/src/domains/PriceDetailsForSSR'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, {
  ProductOption,
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import { InventoryInfoSourceType, IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import RichText from '@src/quince-site-common/src/domains/RichText'
import { ISubscriptionPlan } from '@src/quince-site-common/src/domains/Subscription'
import { ISwatch } from '@src/quince-site-common/src/domains/Swatch'
import productHelper, { THIRD_PARTY } from '@src/quince-site-common/src/helpers/productHelper'
import { AppQRModal, useShopInAppHandler } from '@src/quince-site-common/src/hooks/appQrModal'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useHideKlarnaExp from '@src/quince-site-common/src/hooks/useHideKlarnaExp'
import useKlarnaModule from '@src/quince-site-common/src/hooks/useKlarnaModule'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import B2BProductPurchaseOptions from '@src/quince-site-common/src/organisms/b2b/b2b-product-purchase-options'
import useTrueFitWidgetVisibility from '@src/quince-site-common/src/organisms/true-fit/hooks/useTrueFitWidgetVisibility'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import productService from '@src/quince-site-common/src/services/productService'
import { GROUP_OPTION_NAMES } from '@src/quince-site-common/src/utils/constants'
import { getIsFinalSaleProduct } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import OptionSelectorLegacy from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import useProductPageContext from '@src/quince-site-pdp/src/hooks/useProductContext'
import { getProductDetailsIcons } from '@src/quince-site-pdp/src/organisms/productPageSections/productDetailsAndCare/utils'
import { DISABLE_PARTIAL_V2_DESIGN } from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpDesignV2'
import { getProductPropertiesForTracking, isMiniPDP } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { find } from '@src/utils/functions/utils'
import { getStandardLocale } from '@src/utils/i18n.utils'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import useHash from '../../hooks/useHash'
import { RichTextProps } from '../../molecules'
import { getIsForcedTextOption } from '../../molecules/optionSelector/productVariantSelector/hooks/useProductVariantSelector.helper'
import AirlineCompatibility from '../airlineCompability/AirlineCompability'
import B2BSelfServeProductOfferMessage from '../b2bSelfServeProductOfferMessage/B2BSelfServeProductOfferMessage'
import { BreadCrumb } from '../breadCrumb'
import { default as Button, BUTTON_TYPES } from '../button'
import { GreyBoxes as GreyBoxesLegacy } from '../greyBoxes'
import { Icon } from '../icon'
import ProductDescription from '../product-description/product-description'
import ProductCTALegacy from '../productCTA'
import { CTA_TYPE, LOW_INVENTORY_THRESHOLD } from '../productCTA/productCTAConstants'
import { getUniqueColorGroupings } from '../productCTA/utils'
import { default as ProductInfoLegacy } from '../productInfo'
import { PurchaseType, PURCHASE_TYPES } from '../purchaseTypeSelector'
import SiblingProductOptionsLegacy, { SiblingProductOptionType } from '../siblingProductOptions'
import { SizeGuide } from '../sizeGuide'

import { ProductFormSubscriptionPurchaseSection } from './components/ProductFormSubscriptionPurchaseSection'
import ErrorStateIcon from './icons/errorState.inline.svg'
import HurryIcon from './icons/hurryIcon.inline.svg'
import { RightMiniPDPAddToCart } from './MiniPDPRightContainer'
import * as dsStyles from './product-form-ds.styles'
import { ProductSubscriptionTermsNotice } from './product-subscription-terms-notice'
import { ProductDescFooter } from './ProductDescFooter'
import styles from './productForm.module.scss'
import { ProductOptionProps } from './types'

// @i18n-ignore
const AIRLINE_COMPATIBILITY_HASH = 'airline-compatibility'

function shouldShowOnlySizeChart(
  productOptions: ProductOption[] | undefined,
  sizeChart: ContentfulSizeChart | undefined,
  mergedSizeChart: MergedSizeChart[] | undefined,
  hideSizeChart: boolean | undefined,
) {
  const sizeOption = find(productOptions, ({ name }) => name === ProductOptionName.Size)
  const showOnlySizeChart = !hideSizeChart && sizeOption?.isHidden && !!(sizeChart ?? mergedSizeChart)

  return showOnlySizeChart
}

export interface PreOrderInfo {
  date: string
  onPreOrder: () => void
}

export interface MadeToOrderInfo {
  madeToOrderText: RichText
}

export interface VariantPurchaseInfo {
  availableInventory: number
  showWaitList: boolean
  showMadeToOrder: boolean
  hasJoinedWaitList: boolean
  backInStockDate: string | undefined
  madeToOrderText: Document | undefined
  isPreorder: boolean
  preOrderShippingDate?: string
  selectedVariantId?: number
  isNewPreorderProduct?: boolean
  inventoryInfoSource: InventoryInfoSourceType
  selectedInternalVariantId?: number[]
  isAnySelectedVariantInStock?: boolean
  eddDetail?: EDDDetails
  /**
   * True when we don't yet have authoritative inventory data for the selected
   * variant — either because `productPurchaseInfo` is still being fetched, or
   * because the selected svid isn't yet present in `svidVsSvDetails` (transient
   * state right after a variant switch). Consumers (CTA, OOS banner, waitlist
   * UI) MUST treat this as "don't render OOS / waitlist yet" to avoid the
   * stale-state flash on variant switch in PDP / quick-ATC popups.
   */
  isInventoryLoading?: boolean
}

export interface ProductFormProps {
  className?: string
  title: string
  averageRating?: number
  reviewCount?: number
  productOptions: ProductOption[]
  selectedOptions: ProductVariantOption[]
  isWishListed?: boolean
  formattedSalePrice: string
  festivalOfferPrice?: number
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  formattedFestivalOfferPrice?: string
  formattedTraditionalPrice?: string
  formattedSubscriptionPrice?: string
  subscriptionPrice?: number
  subscriptionFestivalSavingPercentage?: number
  productInfo?: RichTextProps
  productInfoHeaderClassName?: string
  onOptionSelect: (optionName: string, selectedValue: string) => void
  onAddToCart: (isPreorder?: boolean, selectedPlanId?: number) => void
  onWishlistToggle: () => void
  onReviewSummaryClick: () => void
  description?: string
  preOrder?: PreOrderInfo
  hideProductDescription?: boolean
  hideReviewSummary?: boolean
  hideSizeChart?: boolean
  hideOfferMessage?: boolean
  hideMtoText?: boolean
  hideWishListIcon?: boolean
  hidePayLater?: boolean
  hideFooter?: boolean
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  onJoinWaitList: (email?: string | undefined) => void
  sizeChart?: ContentfulSizeChart | undefined
  mergedSizeChart?: MergedSizeChart[] | undefined
  measuringGuides?: ContentfulProductMeasuringGuide[]
  internationalSizeConversionGuides?: ContentfulInternationalSizeConversionGuide[]
  isAddToCartInProgress?: boolean
  isSdcProduct?: boolean
  product?: Product
  errorMessage?: string
  inputOptionError?: string
  optionsContainerClassName?: string
  isReturnable?: boolean
  subDescriptionText?: string
  priceDetails?: PriceDetailsForSSR
  savingPercentage?: number
  offerMessageFromPC?: string
  salePrice: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  selectedSiblingProductId?: number
  onSiblingOptionSelect?: (val: string, productId?: number) => void
  preOrderModalData?: ContentfulPreOrderModal
  disableAddToCart?: boolean
  airlineCompatibilityListings?: CustomSection[] | undefined
  areAllVariantOOS?: boolean
  addToCartRef?: any
  onSwatchLinkClick?: (swatchData: ISwatch[]) => void
  shouldDisableAirlineCompatibility?: boolean
  isCurrentProductOptionLeavingSoon?: boolean
  productPurchaseInfo?: IProductPurchaseInfo
  isPostOrderFlow?: boolean
  handlePostOrderCallback?: () => void
  isQuickATCForMobile?: boolean
  isKeyboardVisibleForQuickATCForMobile?: boolean
  setIsKeyboardVisibleForQuickATCForMobile?: (isKeyboardVisible: boolean) => void
  onClickedShopSimilar?: () => void
  isQuickATCModal?: boolean
  hideSalePrice?: boolean
  isB2BMode?: boolean
  isDOBModalOpen?: boolean
  setIsDOBModalOpen: Dispatch<SetStateAction<boolean>>
  showNewPdpDesignV2?: boolean
  setIsCartPopUpOpen?: (isCartOpen: boolean) => void
  isFromCartPopup?: boolean
  isBuyNow?: boolean
  productCTAType: CTA_TYPE
  expressPayConfig?: ExpressPayConfig
  isB2BSelfService?: boolean
  hidePurchaseTypeSelector?: boolean
  showPurchaseTypeSelector: boolean
  variantSubscriptionPlans: ISubscriptionPlan[]
  purchaseOption: PurchaseType
  setPurchaseOption: (purchaseOption: PurchaseType) => void
  selectedPlanId: number | undefined
  setSelectedPlanId: (selectedPlanId: number | undefined) => void
  showProductSubscriptionDeliveryMessage: boolean
  aboveFoldTrackingRefs?: {
    productInfoRef: React.RefObject<HTMLDivElement>
    colorVariantRef: React.RefObject<HTMLFieldSetElement>
    sizeVariantRef: React.RefObject<HTMLFieldSetElement>
  }
  sizeOptionRef?: React.RefObject<HTMLFieldSetElement>
  atcSectionEndRef?: React.RefObject<HTMLDivElement>
  showBreadCrumb?: boolean
  directory?: Directory
}

const ViewFullProductDetails = ({ text }: { text?: string }) => {
  const { setShowFullPDP } = useProductPageContext()

  function handleClick() {
    setShowFullPDP(true)
    window.scrollTo({
      top: 0,
      // @i18n-ignore
      behavior: 'smooth',
    })
  }

  return (
    <div className={styles.viewFullProdWrapper}>
      <button className={styles.btn} onClick={handleClick}>
        {text ?? 'VIEW FULL PRODUCT DETAILS'}
      </button>
    </div>
  )
}

export const KlarnaWrapper = ({
  hidePayLater,
  salePrice,
  festivalOfferPrice,
  wrapperClassname,
  showNewPdpDesignV2,
}: {
  hidePayLater?: boolean
  salePrice?: number
  festivalOfferPrice?: number
  wrapperClassname?: string
  showNewPdpDesignV2?: boolean
}) => {
  const { locale } = useLocaleContext()
  const localeConfig = useStatsigDynamicLocaleConfig()
  const clientId = localeConfig?.klarnaClientId
  const klarnaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!klarnaRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent(TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_MESSAGE_VIEWED, {
              entity_type: 'action',
              entity_id: 'klarna_payments',
            })
            observer.disconnect()
          }
        })
      },
      { threshold: 0.2 },
    )

    observer.observe(klarnaRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleKlarnaClick = () => {
    trackEvent(TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_LINK_CLICKED, {
      entity_type: 'action',
      entity_id: 'klarna_payments',
    })
  }

  return !hidePayLater && salePrice && clientId ? (
    <div
      ref={klarnaRef}
      className={
        shouldUseDesignSystem()
          ? classnames(wrapperClassname, dsStyles.klarnaWrapperDsClass)
          : classnames(
              wrapperClassname,
              styles.container__szmessage,
              showNewPdpDesignV2 && styles.container__szmessage_newDesignV2,
            )
      }
      onClickCapture={handleKlarnaClick}
    >
      <klarna-placement
        // @i18n-ignore
        data-key="credit-promotion-auto-size"
        // @i18n-ignore
        data-locale={getStandardLocale(locale) || LOCALE_STANDARD_FORMAT}
        data-client-id={clientId}
        data-purchase-amount={Math.floor((festivalOfferPrice ?? salePrice) * 100)}
      />
    </div>
  ) : (
    <></>
  )
}

// The variant pickers route to their DS twins behind the DS flag; legacy otherwise. Scoped to the
// product form (PDP / QATC / mini-PDP) — other consumers of these atoms stay on legacy until migrated.
const OptionSelector = (p: React.ComponentProps<typeof OptionSelectorLegacy>) =>
  shouldUseDesignSystem() ? <OptionSelectorDS {...p} /> : <OptionSelectorLegacy {...p} />
const SiblingProductOptions = (p: React.ComponentProps<typeof SiblingProductOptionsLegacy>) =>
  shouldUseDesignSystem() ? <SiblingProductOptionsDS {...p} /> : <SiblingProductOptionsLegacy {...p} />

// eslint-disable-next-line react/display-name
const ProductForm = React.forwardRef(function ProductFormEl(
  props: ProductFormProps,
  ref: ForwardedRef<HTMLDivElement | null>,
) {
  const { t } = useTranslation('common')
  const productFormRef = useRef<HTMLDivElement>(null)
  const { locale } = useLocaleContext()
  const { storeProgramResponse } = useStorefrontModeContext()
  const { productCTAType } = props

  const newColorGroupings = getUniqueColorGroupings(props.product?.variants ?? [])

  const selectedVariant = useMemo(
    () =>
      props.product
        ? (productHelper.getVariantWithMinQuincePriceForOptions(props.product, props.selectedOptions) ??
          ({} as BaseProductVariant))
        : ({} as BaseProductVariant),
    [props.product, props.selectedOptions],
  )

  useImperativeHandle(ref, () => {
    if (!productFormRef.current) {
      // @i18n-ignore
      throw new Error('productFormRef is not assigned')
    }
    return productFormRef.current
  })

  const {
    showFullPDP,
    setShowFullPDP,
    productPageExpData: {
      productFormWrapperClass,
      moveAddToCartBtnToRight,
      showViewFullProductDetails,
      hideProductDetail,
      hideAddToWishlist,
      hideReviewCount,
      viewFullProductText,
      productInfoHeaderTextClassName,
      hideProductDescFooter,
      hideReviewLabel,
      productFormOptionsClass,
      productInfoReviewClassName,
      inStockMsgClass,
      showReadMoreInDesc,
      showInlineSizeChart,
      hideSizeChart,
    },
  } = useProductPageContext()
  const { hideKlarna } = useHideKlarnaExp()
  const isFabricMergedProduct = props.product?.addOnProductOptionGroups?.find(
    option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC,
  )
  const initialSwatch = isFabricMergedProduct ? [] : [props.product?.swatch]

  const classes = classnames(
    styles.container,
    props.className,
    productFormWrapperClass,
    props.isQuickATCModal && styles.container_quickATCModal,
  )
  const [showSizeChart, setShowSizeChart] = useState(false)
  const [showAirlineCompatibility, setShowAirlineCompatibility] = useState(false)
  const [swatches, setSwatches] = useState(initialSwatch)
  const { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal } = useShopInAppHandler()
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = props.expressPayConfig && !props.expressPayConfig.disabled && isExpressPayEnabled
  const shouldKeepDefaultDesktopExpressPayRowMounted =
    showExpressPay && !props.isQuickATCForMobile && !props.isPostOrderFlow

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const isVariantThirdParty = selectedVariant.vendorType === THIRD_PARTY

  const fetchProductWithSwatch = async () => {
    const products = await productService.getProducts({
      spids: props.product?.addOnProductOptionGroups
        ?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC)
        ?.groupItems.map(item => item.spid),
    })
    let currentFabricSelectedSwatch
    const productSwatches = products
      .filter(product => {
        if (product.spid == props.product?.spid) {
          currentFabricSelectedSwatch = product.swatch
          return false
        } else {
          return true
        }
      })
      .map(product => product.swatch)
    setSwatches([currentFabricSelectedSwatch, ...productSwatches])
  }
  useEffect(() => {
    if (props.product?.addOnProductOptionGroups?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC))
      //eslint-disable-next-line
      fetchProductWithSwatch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.product])

  const [hash, _setHashValue] = useHash()

  useEffect(() => {
    if (hash === AIRLINE_COMPATIBILITY_HASH) {
      displayAirlineCompatbility()
    }
  }, [hash])

  const handleSizeChart = () => {
    const productProperties = getProductPropertiesForTracking(props.product as Product)
    trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED, {
      section_type: 'product_form',
      context: {
        size_chart_name: props.product?.sizeChart?.title,
        productProperties: productProperties,
      },
    })
    setShowSizeChart(true)
  }

  const displayAirlineCompatbility = () => {
    setShowAirlineCompatibility(true)
  }

  const hideAirlineCompatbility = () => {
    setShowAirlineCompatibility(false)
    // Clear the hash when modal is closed so it can be reopened
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.hash = ''
      window.history.replaceState(null, '', url.toString())
      // Manually dispatch a hashchange event to update the useHash hook
      window.dispatchEvent(new HashChangeEvent('hashchange'))
    }
  }

  const selectedVariantAvailableInventory = props.selectedVariantPurchaseInfo?.availableInventory ?? 0

  const renderSwatchSelectionButton = useCallback(() => {
    return swatches.filter(item => item).length ? (
      <button
        className={styles.freeSwatchText}
        onClick={() => {
          props.onSwatchLinkClick?.(swatches.filter(item => !!item))
        }}
      >
        {t('order_free_swatches')}
      </button>
    ) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swatches, props.onSwatchLinkClick])

  useKlarnaModule(props.salePrice, locale)

  const { showTruefitWidget } = useTrueFitWidgetVisibility(props.product)

  const isDS = shouldUseDesignSystem()
  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          className={
            isDS
              ? dsStyles.optionGroupDsClass
              : classnames(styles.container__options, styles.container__options_adjustedPadding)
          }
          labelClassName={isDS ? dsStyles.labelDsClass : styles.container__label}
          optionSelectorClassName={
            isDS
              ? undefined
              : classnames(
                  styles.container__options__option,
                  styles.container__options__option_adjustedPadding,

                  group.groupOptionName !== ProductOptionName.Color &&
                    group.groupOptionName !== GROUP_OPTION_NAMES.FABRIC &&
                    styles.container__options__option_adjustedPadding_size,
                )
          }
          optionClassname={isDS ? undefined : styles.container__options__optionWrapper}
          showNewPdpDesignV2={props.showNewPdpDesignV2}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
    }
  })

  const _productOptions = props.productOptions
  let updatedProductOptions = _productOptions
  if (newColorGroupings.length && props.product) {
    updatedProductOptions = productHelper.updateProductOptionsWithNewColorOptions(
      props.product,
      props.productOptions,
      newColorGroupings,
      t,
    ) as ProductOption[]

    updatedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
  }

  const colorOptionLastIndex = updatedProductOptions.findLastIndex(po => po.name == ProductOptionName.Color)

  const productOptions = updatedProductOptions
    .filter(po => !po.isHidden)
    .map((productOption, index, arr) => {
      const shouldReduceMarginBottom =
        (newColorGroupings.length && index + 1 <= arr.length - 1 && arr[index + 1]?.name == ProductOptionName.Color) ||
        false

      const shouldShowSwatchButton = colorOptionLastIndex === index && !props.isBuyNow

      return {
        node: renderProductOption({
          productOption: productOption,
          selectedOptions: props.selectedOptions,
          onOptionSelect: props.onOptionSelect,
          sizeChart: props.sizeChart,
          mergedSizeChart: props.mergedSizeChart,
          handleSizeChart: handleSizeChart,
          hideSizeChart: props.hideSizeChart ?? hideSizeChart,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          renderSwatchSelectionButton: shouldShowSwatchButton ? renderSwatchSelectionButton : () => null,
          optionsContainerClassName: props.optionsContainerClassName,
          productFormOptionsClass: productFormOptionsClass,
          shouldReduceMarginBottom: shouldReduceMarginBottom,
          showInlineSizeChart: showInlineSizeChart,
          showTruefitWidget: showTruefitWidget,
          product: props.product,
          t: t,
          productPurchaseInfo: props.productPurchaseInfo,

          inputOptionError: props.inputOptionError,
          showNewPdpDesignV2: props.showNewPdpDesignV2,
          isB2BSelfService: props.isB2BSelfService,
          colorVariantRef: props.aboveFoldTrackingRefs?.colorVariantRef,
          sizeVariantRef: props.aboveFoldTrackingRefs?.sizeVariantRef,
          sizeOptionRef: props.sizeOptionRef,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }
    })

  const combinedProductOptions = [...(addOnOptions ?? []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const isFinalSaleProduct = getIsFinalSaleProduct(props.product, props.selectedOptions)

  const isB2BFinalSaleProduct =
    props.isB2BSelfService && (storeProgramResponse?.storeProgramConfig.isFinalSale ?? false)

  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < LOW_INVENTORY_THRESHOLD

  const showOnlySizeChart = shouldShowOnlySizeChart(
    props.productOptions,
    props.sizeChart,
    props.mergedSizeChart,
    props.hideSizeChart ?? hideSizeChart,
  )

  const hideSavings = props.product?.hideSavings ?? false
  const isMiniPDPPage = props?.product?.slug ? isMiniPDP(props.product.slug) : false
  const productDetailsIcons = getProductDetailsIcons(props.product as Product)

  const showProductDescription = !hideProductDetail && !props.hideProductDescription && props.description

  const deviceInfo = useDeviceInfo()
  const isPDP = !props.isFromCartPopup && !props.isBuyNow && !props.isPostOrderFlow

  return (
    <>
      <div ref={productFormRef} className={classes}>
        {props.showBreadCrumb && props.directory && (
          <div className={styles.container__breadCrumb}>
            <BreadCrumb
              directory={props.directory}
              deviceType={deviceInfo?.deviceType}
              showNewPdpDesignV2={props.showNewPdpDesignV2}
              isPDP={isPDP}
            />
          </div>
        )}
        {(() => {
          const productInfoProps = {
            product: props.product,
            productInfoHeaderClassName: props.productInfoHeaderClassName,
            hideReviewSummary: props.hideReviewSummary,
            productTitle: props.title,
            formattedSalePrice: props.formattedSalePrice,
            formattedTraditionalPrice: props.formattedTraditionalPrice,
            isWishListed: props.isWishListed,
            avgRating: props.averageRating,
            reviewCount: props.reviewCount,
            onWishListToggle: props.onWishlistToggle,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            hideWishListIcon: props.hideWishListIcon || hideAddToWishlist || props.showNewPdpDesignV2,
            onReviewSummaryClick: props.onReviewSummaryClick,
            savingPercentage: props.savingPercentage,
            brandName: props.brandName,
            brandLink: props.brandLink,
            traditionalDiscountMessage: props.traditionalDiscountMessage,
            formattedFestivalOfferPrice: props.formattedFestivalOfferPrice,
            showFullPDP,
            hideReviewCount,
            hideReviewLabel,
            productInfoHeaderTextClassName,
            productInfoReviewClassName,
            festivalPricePerUnit: props.festivalPricePerUnit,
            subscriptionOfferPricePerUnit: props.subscriptionOfferPricePerUnit,
            pricePerServing: selectedVariant?.pricePerServing,
            traditionalPricePerServing: selectedVariant?.traditionalPricePerServing,
            hideSavings,
            hideSalePrice: props.hideSalePrice,
            showNewPdpDesignV2: !DISABLE_PARTIAL_V2_DESIGN,
            isFinalSaleProduct,
            isB2BFinalSaleProduct,
            formattedSubscriptionPrice: props.formattedSubscriptionPrice,
            purchaseOption: props.purchaseOption,
            updatedDesignForV2: props.showNewPdpDesignV2,
            containerRef: props.aboveFoldTrackingRefs?.productInfoRef,
            isVariantThirdParty,
            selectedOptions: props.selectedOptions,
          }
          return shouldUseDesignSystem() ? (
            <ProductInfoDS {...productInfoProps} />
          ) : (
            <ProductInfoLegacy {...productInfoProps} />
          )
        })()}

        {combinedProductOptions.map(option => (
          <Fragment key={option.key}>{option.node}</Fragment>
        ))}
        {showOnlySizeChart && renderSizeChartButton(handleSizeChart, t, undefined, props.showNewPdpDesignV2, true)}
        {props.isB2BSelfService ? (
          <B2BSelfServeProductOfferMessage showNewPdpDesignV2={props.showNewPdpDesignV2} />
        ) : (
          (() => {
            const greyBoxesProps = {
              product: props.product,
              productOptions: props.productOptions,
              selectedOptions: props.selectedOptions,
              selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
              productCTAType,
              isReturnable: props.isReturnable,
              subDescriptionText: props.subDescriptionText,
              hideOfferMessage: props.hideOfferMessage,
              offerMessageFromPC: props.offerMessageFromPC,
              hideMtoText: props.hideMtoText,
              isFinalSaleProduct,
              handleShopInApp,
              preOrderModalData: props.preOrderModalData,
              showNewPdpDesignV2: props.showNewPdpDesignV2,
              isQuickATCModal: props.isQuickATCModal ?? props.isQuickATCForMobile,
            }
            return shouldUseDesignSystem() ? (
              <GreyBoxesDS {...greyBoxesProps} />
            ) : (
              <GreyBoxesLegacy {...greyBoxesProps} />
            )
          })()
        )}
        <ProductFormSubscriptionPurchaseSection
          showProductSubscriptionDeliveryMessage={props.showProductSubscriptionDeliveryMessage}
          subscribedPlan={selectedVariant?.subscription?.subscribedPlan}
          showNewPdpDesignV2={props.showNewPdpDesignV2}
          selectedVariantPurchaseInfoShowWaitList={props.selectedVariantPurchaseInfo?.showWaitList}
          productCTAType={productCTAType}
          isRecurringItemSubscriptionsExpEnabled={isRecurringItemSubscriptionsExpEnabled}
          hidePurchaseTypeSelector={props.hidePurchaseTypeSelector}
          subscriptionEnabled={props.product?.subscriptionEnabled}
          productPurchaseInfo={props.productPurchaseInfo}
          showPurchaseTypeSelector={props.showPurchaseTypeSelector}
          product={props.product}
          purchaseOption={props.purchaseOption}
          setPurchaseOption={props.setPurchaseOption}
          selectedPlanId={props.selectedPlanId}
          setSelectedPlanId={props.setSelectedPlanId}
          isFromCartPopup={props.isFromCartPopup}
          isBuyNow={props.isBuyNow}
          variantSubscriptionPlans={props.variantSubscriptionPlans}
          formattedSubscriptionPrice={props.formattedSubscriptionPrice}
          formattedSalePrice={props.formattedSalePrice}
          formattedFestivalOfferPrice={props.formattedFestivalOfferPrice}
          subscriptionFestivalSavingPercentage={props.subscriptionFestivalSavingPercentage}
          subscriptionId={selectedVariant?.subscription?.subscribedPlan?.subscriptionId}
          showVariantAvailableInventory={showVariantAvailableInventory}
          selectedVariantAvailableInventory={selectedVariantAvailableInventory}
          isQuickATCForMobile={props.isQuickATCForMobile}
        />
        {showVariantAvailableInventory && (
          <div
            className={
              isDS
                ? dsStyles.lowInventoryMessageDsClass
                : classnames(
                    styles.selected_variant_available_inventory_message,
                    styles.selected_variant_available_inventory_message_adjustedPadding,
                    props.isQuickATCForMobile && styles.selected_variant_available_inventory_message__quickATC,
                    props.showNewPdpDesignV2 && styles.selected_variant_available_inventory_message_newDesignV2,
                  )
            }
          >
            {isDS ? (
              <Icon iconSvg={HurryIcon} className={dsStyles.lowInventoryIconDsClass} />
            ) : props.showNewPdpDesignV2 ? (
              <Icon iconSvg={HurryIcon} />
            ) : null}
            {t('hurry_only', { quantity: selectedVariantAvailableInventory })}
          </div>
        )}

        {props.isB2BSelfService && (
          <B2BProductPurchaseOptions
            product={props.product}
            selectedOptions={props.selectedOptions}
            productPurchaseInfo={props.productPurchaseInfo}
          />
        )}

        {!moveAddToCartBtnToRight && !props.isB2BSelfService && (
          <>
            {props.errorMessage &&
              (isDS ? (
                <RenderErrorMessageDS
                  errorMessage={props.errorMessage}
                  isQuickATCForMobile={props.isQuickATCForMobile}
                  showNewPdpDesignV2
                />
              ) : (
                <div
                  className={classnames(
                    styles.container__error_message,
                    isMiniPDPPage && props.isQuickATCForMobile && styles.container__error_message__miniPdpError,
                    props.isQuickATCForMobile && styles.container__error_message__quickATC,
                    props.showNewPdpDesignV2 && styles.container__error_message_newDesignV2,
                  )}
                >
                  {props.showNewPdpDesignV2 ? <Icon iconSvg={ErrorStateIcon} /> : null}
                  {props.errorMessage}
                </div>
              ))}
            {(() => {
              const productCTAProps = {
                type: productCTAType,
                isQuickATCModal: props.isQuickATCModal,
                onAddToCart: props.onAddToCart,
                isAddToCartInProgress: props.isAddToCartInProgress,
                selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
                onJoinWaitList: props.onJoinWaitList,
                showVariantAvailableInventory,
                product: props.product,
                areAllVariantOOS: props.areAllVariantOOS,
                addToCartRef: props.addToCartRef,
                isCurrentProductOptionLeavingSoon: props.isCurrentProductOptionLeavingSoon,
                isPostOrderFlow: props.isPostOrderFlow,
                handlePostOrderCallback: props.handlePostOrderCallback,
                isQuickATCForMobile: props.isQuickATCForMobile,
                isKeyboardVisibleForQuickATCForMobile: props.isKeyboardVisibleForQuickATCForMobile,
                setIsKeyboardVisibleForQuickATCForMobile: props.setIsKeyboardVisibleForQuickATCForMobile,
                onClickedShopSimilar: props.onClickedShopSimilar,
                disableAddToCart: props.disableAddToCart,
                showNewPdpDesignV2: props.showNewPdpDesignV2,
                selectedOptions: props.selectedOptions,
                setIsCartPopUpOpen: props.setIsCartPopUpOpen,
                isFromCartPopup: props.isFromCartPopup,
                handleShopInApp,
                salePrice: props.festivalOfferPrice ?? props.salePrice,
                subscriptionPrice: props.subscriptionPrice,
                expressPayConfig: props.expressPayConfig,
                enableExpressPayAnimation: true,
                hideKlarna: props.isQuickATCForMobile
                  ? false
                  : hideKlarna || (props.showPurchaseTypeSelector && props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE),
                purchaseOption: props.purchaseOption,
                selectedPlanId: props.selectedPlanId,
                showPurchaseTypeSelector: props.showPurchaseTypeSelector,
                atcSectionEndRef: props.atcSectionEndRef,
              }
              return shouldUseDesignSystem() ? (
                <ProductCTADS {...productCTAProps} />
              ) : (
                <ProductCTALegacy {...productCTAProps} />
              )
            })()}
          </>
        )}
        {showViewFullProductDetails ? <ViewFullProductDetails text={viewFullProductText} /> : <></>}
        {moveAddToCartBtnToRight ? null : (
          <>
            {!(hideKlarna || (props.showPurchaseTypeSelector && props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) && (
              <KlarnaWrapper
                hidePayLater={props.hidePayLater}
                salePrice={props.salePrice}
                festivalOfferPrice={props.festivalOfferPrice}
                showNewPdpDesignV2={props.showNewPdpDesignV2}
              />
            )}
            {shouldKeepDefaultDesktopExpressPayRowMounted &&
              isRecurringItemSubscriptionsExpEnabled &&
              props.showPurchaseTypeSelector &&
              props.product?.subscriptionEnabled &&
              props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE && <ProductSubscriptionTermsNotice />}
            {!hideProductDescFooter && !props.isBuyNow && (
              <ProductDescFooter
                hideFooter={props.hideFooter}
                isReturnable={props.isReturnable}
                product={props.product}
                selectedOptions={props.selectedOptions}
                showNewPdpDesignV2={props.showNewPdpDesignV2}
                isFinalSaleProduct={isFinalSaleProduct}
                isB2BSelfService={props.isB2BSelfService}
              />
            )}
          </>
        )}
        {showProductDescription && (
          <ProductDescription
            description={props.description ?? ''}
            showNewPdpDesignV2={!!props.showNewPdpDesignV2}
            productDetailsIcons={productDetailsIcons}
            showReadMoreInDesc={showReadMoreInDesc}
            isB2BMode={props.isB2BMode}
          />
        )}
      </div>
      <RightMiniPDPAddToCart
        shouldShow={moveAddToCartBtnToRight}
        hidePayLater={props.hidePayLater}
        salePrice={props.salePrice}
        festivalOfferPrice={props.festivalOfferPrice}
        type={productCTAType}
        onAddToCart={props.onAddToCart}
        isAddToCartInProgress={props.isAddToCartInProgress}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        onJoinWaitList={props.onJoinWaitList}
        showVariantAvailableInventory={showVariantAvailableInventory}
        product={props.product}
        hideFooter={props.hideFooter}
        isReturnable={props.isReturnable}
        setShowFullPDP={setShowFullPDP}
        hideKlarna={hideKlarna || (props.showPurchaseTypeSelector && props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE)}
        errorMessage={props.errorMessage}
        inStockMsgClass={inStockMsgClass}
        purchaseOption={props.purchaseOption}
        selectedPlanId={props.selectedPlanId}
        showPurchaseTypeSelector={props.showPurchaseTypeSelector}
        showNewPdpDesignV2={props.showNewPdpDesignV2}
      />
      {props.sizeChart && showSizeChart && (
        <SizeGuide
          showSizeChartModal={showSizeChart}
          sizeChart={props.sizeChart}
          mergedSizeChart={props.mergedSizeChart ?? []}
          onSizeChartModalClose={() => {
            setShowSizeChart(false)
          }}
          measuringGuides={props.measuringGuides ?? []}
          internationalSizeConversionGuides={props.internationalSizeConversionGuides ?? []}
          product={props.product}
        />
      )}
      {!props.shouldDisableAirlineCompatibility && showAirlineCompatibility && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatbility}
          airlineCompatibilityListingCS={props.airlineCompatibilityListings}
        />
      )}
      <AppQRModal
        isOpen={isAppQRModalOpen}
        product={props.product}
        selectedOptions={props.selectedOptions}
        onClose={handleCloseAppQRModal}
      />
    </>
  )
})

const renderSizeChartButton = (
  handleSizeChart: (e: React.MouseEvent<HTMLButtonElement>) => void,
  t: TFunction,
  showInlineSizeChart?: boolean,
  showNewPdpDesignV2?: boolean,
  isStandalone?: boolean,
) => (
  <Button
    type={BUTTON_TYPES.SECONDARY_LINK}
    onClick={handleSizeChart}
    className={classnames(styles.container__label__sizeChart, styles.container__label__sizeChart_adjustedPadding, {
      [styles.sizeChart_newDesignV2 ?? '']: showNewPdpDesignV2,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      [styles.sizeChart_show ?? '']: showInlineSizeChart || showNewPdpDesignV2,
      [styles.sizeChart_standalone ?? '']: isStandalone,
    })}
    id={TEST_LOCATORS.SIZE_CHART_BUTTON}
  >
    {t('size_chart')}
  </Button>
)

export const documentToReactComponentsTextOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => {
      // eslint-disable-next-line react/no-children-prop
      return React.Children.map(children, (child: React.ReactNode, index: number) => {
        if (typeof child === 'string') {
          // eslint-disable-next-line
          return <span key={index} dangerouslySetInnerHTML={{ __html: getSanitizedHtml(child) }} />
        }
        return child
      })
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => {
      return typeof text === 'string' ? (
        // eslint-disable-next-line
        <span className="font-weight--500" dangerouslySetInnerHTML={{ __html: getSanitizedHtml(text) }} />
      ) : (
        text
      )
    },
  },
}

// @i18n-ignore
const FIRMNESS_DISPLAY_NAME = 'Firmness'
const FIRMNESS_DISPLAY_NAME_FR = 'Fermeté'
// @i18n-ignore
const VALUE_DISPLAY_NAME = 'Value'
const VALUE_DISPLAY_NAME_FR = 'Valeur'

export const renderProductOption = (props: ProductOptionProps) => {
  const {
    selectedOptions,
    onOptionSelect,
    sizeChart,
    mergedSizeChart,
    handleSizeChart,
    hideSizeChart,
    optionNameSelectedValueMap,
    renderSwatchSelectionButton,
    productFormOptionsClass,
    shouldReduceMarginBottom,
    showInlineSizeChart,
    // showTruefitWidget,
    product,
    legendClassName,
    optionSelectorClassName,
    optionDisplayTypesVsClassName,
    t,
    productPurchaseInfo,
    inputOptionError,
    showNewPdpDesignV2,
    noMarginForSizeOption,
    isBundle,
    isB2BSelfService,
    colorVariantRef,
    sizeVariantRef,
    sizeOptionRef,
    showMoreOption,
    optionLimit,
    onShowMoreClick,
    excessOptionsTextClassName,
    isMiniPDP,
  } = props
  let productOption = props.productOption
  if (getIsForcedTextOption(product?.id as number)) {
    productOption = new ProductOption({
      ...productOption,
      values: productOption.values?.map(v => {
        return { ...v, displayType: ProductOptionDisplayType.TEXT }
      }),
    })
  }
  const poValueDisplayNameMap = productOption.values?.reduce((map: Record<string, string>, option) => {
    map[option.value] = option.displayName ?? option.value
    return map
  }, {})
  // TODO(STRFRNT-2867) - remove this after release
  let optionValues: ProductOptionValue[] = []
  const isCustomizationInputOption =
    productOption.name === ProductOptionName.Customization && productOption.displayType === 'Input'
  const isEngravingInputOption =
    productOption.name === ProductOptionName.Engraving && productOption.displayType === 'Text_box'

  if (isCustomizationInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.DEPRECATED_INPUT,
        value: '',
      }),
    ]
  } else if (isEngravingInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.INPUT,
        value: '',
        maxCharacterLimit: productOption.maxCharacterLimit,
        minCharacterLimit: productOption.minCharacterLimit,
      }),
    ]
  } else {
    optionValues = productOption.values ?? []
  }

  if (productOption.isHidden) return null

  // Hardcoding required as productOption.Name comes from API and can be used for tracking
  // Firmness productOptionName is color
  // In giftcard options the displayname will be locale based, thus adding the fr-ca display name which will conditionally add a css class
  const isAllRoundOptions = (() => {
    const excludedDisplayNames = [
      FIRMNESS_DISPLAY_NAME,
      VALUE_DISPLAY_NAME,
      VALUE_DISPLAY_NAME_FR,
      FIRMNESS_DISPLAY_NAME_FR,
    ]
    return productOption.name === ProductOptionName.Color && !excludedDisplayNames.includes(productOption.displayName)
  })()
  const optionSelectedValue = optionNameSelectedValueMap[productOption.name]
  const isDS = shouldUseDesignSystem()

  const displayName = productOption.displayName || productOption.name

  const optionDisplayName =
    isB2BSelfService && ProductOptionName.Size === displayName
      ? shouldUseEnterQuantityLabel(optionValues)
        ? `${t('enter')} ${t('quantity')}`
        : `${t('enter')} ${displayName} ${t('&')} ${t('quantity')}`
      : displayName

  const optionTrackingRef =
    productOption.name === ProductOptionName.Color
      ? colorVariantRef
      : productOption.name === ProductOptionName.Size
        ? sizeVariantRef
        : undefined
  const mergedOptionRef = (element: HTMLFieldSetElement | null) => {
    if (optionTrackingRef) {
      ;(optionTrackingRef as React.MutableRefObject<HTMLFieldSetElement | null>).current = element
    }

    if (productOption.name == ProductOptionName.Size && sizeOptionRef) {
      ;(sizeOptionRef as React.MutableRefObject<HTMLFieldSetElement | null>).current = element
    }
  }

  return (
    <fieldset
      ref={mergedOptionRef}
      className={classnames(
        productFormOptionsClass,
        isDS
          ? dsStyles.optionGroupDsClass
          : classnames(styles.container__options, styles.container__options_adjustedPadding, {
              [styles.container__options__colorOptionGroup ?? '']: shouldReduceMarginBottom,
              [styles.container__options__noMarginForSizeOption ?? '']: noMarginForSizeOption,
              // B2B self-serve only, size option only: extra space before the size/qty grid.
              // Top margin collapses with the color option's bottom margin so the larger value wins.
              // Constant 32px (px, not rem — legacy html font-size is viewport-relative; see normalize.scss).
              [styles.container__options__b2bSelfServeSizeSpacing ?? '']:
                isB2BSelfService && productOption.name === ProductOptionName.Size,
            }),
      )}
      key={productOption.name}
    >
      <legend
        data-testid={`legend_${productOption.displayName}`}
        className={classnames(
          isDS
            ? dsStyles.labelDsClass
            : classnames(styles.container__label, showNewPdpDesignV2 && styles.container__label_newPdpDesignV2),
          legendClassName,
        )}
      >
        <span className={isDS ? dsStyles.optionLabelDsClass : styles.container__label__optionLabel}>
          {optionDisplayName}
          {/* TODO(STRFRNT-2867) - remove this after release */}
          {productOption.displayType === 'Text Box'
            ? t('var1_v6', { var1: optionNameSelectedValueMap[productOption.name] ?? '' })
            : ''}
          {productOption.displayType === 'Input' || productOption.displayType === 'Text Box' ? '' : ': '}
          {optionSelectedValue && poValueDisplayNameMap?.[optionSelectedValue] ? (
            <span className={isDS ? dsStyles.selectedValueDsClass : styles.container__label__selectedValue}>
              {poValueDisplayNameMap[optionSelectedValue]}
            </span>
          ) : (
            <span className="sr-only">{t('not_selected')}</span>
          )}
        </span>
        {!hideSizeChart &&
          productOption.name === ProductOptionName.Size &&
          (sizeChart ?? mergedSizeChart) &&
          renderSizeChartButton(handleSizeChart, t, showInlineSizeChart, showNewPdpDesignV2)}
      </legend>
      <OptionSelector
        className={
          isDS
            ? optionSelectorClassName // OptionSelectorDS supplies its own row layout + gaps
            : classnames(
                styles.container__options__option,
                optionSelectorClassName,
                styles.container__options__option_adjustedPadding,
                productOption.name !== ProductOptionName.Color &&
                  styles.container__options__option_adjustedPadding_size,
                showNewPdpDesignV2 && styles.container__options__option_newDesignV2,
              )
        }
        optionValueList={optionValues}
        selectedValue={optionNameSelectedValueMap[productOption.name]}
        onSelect={value => {
          onOptionSelect(productOption.name, value)
        }}
        isAllRoundOptions={isAllRoundOptions}
        product={product}
        selectorKey={QUINCE_BOT_LOCATORS.PDP.FIRST_PRODUCT_OPTIONS}
        selectedOptions={selectedOptions}
        isPDP={true}
        optionDisplayTypesVsClassName={optionDisplayTypesVsClassName}
        renderAfterColorComponent={renderSwatchSelectionButton}
        displayName={productOption.displayName}
        productPurchaseInfo={productPurchaseInfo}
        inputOptionError={inputOptionError}
        showNewPdpDesignV2={showNewPdpDesignV2}
        isBundle={isBundle}
        optionName={productOption.name}
        isB2BSelfService={isB2BSelfService}
        showMoreOption={showMoreOption}
        optionLimit={optionLimit}
        onShowMoreClick={onShowMoreClick}
        excessOptionsTextClassName={excessOptionsTextClassName}
        isMiniPDP={isMiniPDP}
      />
      {/* {!showInlineSizeChart &&
        !hideSizeChart &&
        productOption.name === ProductOptionName.Size &&
        (sizeChart ?? mergedSizeChart) && (
          <>
            {showTruefitWidget ? (
              <TrueFitWidget
                productId={product?.id}
                colorId={getSelectedColorFromSelectedOptions(selectedOptions)}
                useDataRec
                // @i18n-ignore
                parentContainerId="product_purchase"
              />
            ) : null}
          </>
        )} */}
    </fieldset>
  )
}

export const renderErrorMessage = (errorMessage?: string, className?: string, showNewPdpDesignV2?: boolean) => {
  return (
    <div
      className={classnames(
        styles.container__error_message,
        className,
        showNewPdpDesignV2 && styles.container__error_message_newDesignV2,
      )}
    >
      {showNewPdpDesignV2 ? <Icon iconSvg={ErrorStateIcon} /> : null}
      {errorMessage}
    </div>
  )
}

export default ProductForm
/* eslint-enable i18next/no-literal-string */
